import React, { useEffect, useMemo, useState } from 'react';
import { Input, Modal, Select, Spin, Tag, Tooltip } from 'antd';
import {
  SearchOutlined, StarFilled, StarOutlined, DeleteOutlined, PictureOutlined, UploadOutlined,
  LeftOutlined, RightOutlined, HolderOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';
import usePermissions from '../../hooks/usePermissions';
import useRefLists from '../rapports/useRefLists';
import {
  NdoePage, NdoePageHeader, NdoeImage, NdoeEmpty, NdoeLoading, NdoeError,
} from '../../components/ndoe';
import './photosPartage.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const POINTURES = Array.from({ length: 13 }, (_, i) => 34 + i); // 34..46

// Photos « à partager » (mannequin) — jusqu'à 5 photos PAR MODÈLE, stockées à
// part des photos catalogue. La page Partage catalogue les affiche en priorité.
// Règles (spec 15/07) : première photo = défaut ; défaut changeable d'un clic ;
// « Préférer la photo catalogue » = aucun défaut ; suppression du défaut →
// la suivante est promue. Accès : permission photos_partage.gerer (matrice).
const PhotosPartage = () => {
  const { ready, can } = usePermissions();
  const autorise = can('photos_partage.gerer');

  const [q, setQ] = useState('');
  // Mêmes critères que le Catalogue de partage (WhatsApp) : filtres
  // MULTI-sélection + tri serveur — demande du 18/07.
  const refs = useRefLists(true);
  const [filters, setFilters] = useState({ id_categorie: [], id_marque: [], id_couleur: [], taille: [] });
  const [tri, setTri] = useState('');
  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v || [] }));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [modele, setModele] = useState(null);          // modèle ouvert dans l'éditeur
  const [photos, setPhotos] = useState(null);          // { loading, list }
  const [action, setAction] = useState(false);         // mutation en cours
  const [dragIndex, setDragIndex] = useState(null);    // slot en cours de glissement (réordonner)
  const [fileDrop, setFileDrop] = useState(false);     // survol d'un fichier image sur la zone

  useEffect(() => {
    if (!autorise) { setLoading(false); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true); setErreur(null);
      try {
        const p = new URLSearchParams({ limit: '400' });
        if (q.trim()) p.set('q', q.trim());
        if (tri) p.set('sort', tri);
        // Listes « 42,44,37 » -> IN côté serveur (même contrat que le Catalogue
        // de partage). Liste vide -> paramètre omis. PAS de filtre dispo ici :
        // on gère les photos de TOUS les modèles, ruptures comprises.
        Object.entries(filters).forEach(([k, v]) => {
          if (Array.isArray(v) && v.length) p.set(k, v.join(','));
        });
        const { data } = await axios.get(`${DOMAIN}/api/catalogue/stock?${p.toString()}`);
        if (alive) setRows(data?.variantes || []);
      } catch (e) { if (alive) setErreur(e.response?.data?.error || e.message); }
      finally { if (alive) setLoading(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, filters, tri, autorise]);

  // Un modèle = une carte (les photos mannequin sont PAR MODÈLE, pas par couleur).
  // On regroupe avec une Map (PAS un objet) pour PRÉSERVER l'ordre du serveur :
  // un objet indexé par id_produit (entier) renvoie ses valeurs triées par clé
  // numérique croissante via Object.values → le tri serveur (meilleures ventes,
  // stock dormant, etc.) était silencieusement écrasé (bug « le tri ne marche pas »).
  const modeles = useMemo(() => {
    const parId = new Map();
    rows.forEach((v) => {
      let g = parId.get(v.id_produit);
      if (!g) {
        g = {
          id_produit: v.id_produit, nom_produit: v.nom_produit,
          code_produit: v.code_produit, marque: v.marque,
          img: v.img, img_partage: v.img_partage || null,
        };
        parId.set(v.id_produit, g);
      }
      if (!g.img && v.img) g.img = v.img;
      if (!g.img_partage && v.img_partage) g.img_partage = v.img_partage;
    });
    return [...parId.values()];
  }, [rows]);

  const chargerPhotos = async (m) => {
    setModele(m);
    setPhotos({ loading: true, list: [] });
    try {
      const { data } = await axios.get(`${DOMAIN}/api/photos-partage/${m.id_produit}`);
      setPhotos({ loading: false, list: data?.photos || [], max: data?.max || 5 });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Chargement impossible.');
      setPhotos({ loading: false, list: [] });
    }
  };

  const rafraichir = async () => { if (modele) await chargerPhotos(modele); };

  // Ajout d'une ou plusieurs images (clic ou dépôt de fichiers). Le point
  // d'entrée API prend UNE photo : on envoie en série, borné aux emplacements
  // restants (max 5).
  const televerserPlusieurs = async (files) => {
    if (!modele || action) return;
    const dejaLa = photos?.list || [];
    const maxi = photos?.max || 5;
    const restant = Math.max(0, maxi - dejaLa.length);
    if (restant <= 0) { toast.info(`Maximum ${maxi} photos à partager par modèle.`); return; }
    const aEnvoyer = files.slice(0, restant);
    if (files.length > restant) toast.info(`Seules ${restant} photo(s) ajoutée(s) (maximum ${maxi}).`);
    setAction(true);
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const f of aEnvoyer) {
        const form = new FormData();
        form.append('photo', f);
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${DOMAIN}/api/photos-partage/${modele.id_produit}`, form);
      }
      toast.success(`${aEnvoyer.length} photo(s) ajoutée(s).`);
      await rafraichir();
    } catch (e) { toast.error(e.response?.data?.error || 'Téléchargement impossible.'); await rafraichir(); }
    finally { setAction(false); }
  };

  const definirDefaut = async (idPhoto) => {
    if (!modele || action) return;
    setAction(true);
    try {
      const { data } = await axios.put(`${DOMAIN}/api/photos-partage/${modele.id_produit}/defaut`, { id_photo: idPhoto });
      toast.success(data?.message || 'Défaut mis à jour.');
      await rafraichir();
    } catch (e) { toast.error(e.response?.data?.error || 'Impossible.'); }
    finally { setAction(false); }
  };

  const supprimer = async (idPhoto) => {
    if (!modele || action) return;
    setAction(true);
    try {
      await axios.delete(`${DOMAIN}/api/photos-partage/photo/${idPhoto}`);
      toast.success('Photo supprimée.');
      await rafraichir();
    } catch (e) { toast.error(e.response?.data?.error || 'Suppression impossible.'); }
    finally { setAction(false); }
  };

  // Réordonnancement (glisser-déposer ou flèches) : mise à jour OPTIMISTE puis
  // persistance de la nouvelle suite complète des id. En cas d'échec on relit la
  // vérité serveur. `est_defaut` (★) est indépendant de l'ordre : il suit sa
  // photo, jamais réattribué par un déplacement.
  const reordonner = async (fromIdx, toIdx) => {
    const avant = photos?.list || [];   // instantané de l'ordre courant (référence non mutée)
    if (fromIdx == null || toIdx == null || fromIdx === toIdx) return;
    if (toIdx < 0 || toIdx >= avant.length || action) return;
    const cur = [...avant];
    const [moved] = cur.splice(fromIdx, 1);
    cur.splice(toIdx, 0, moved);
    setPhotos((p) => (p ? { ...p, list: cur } : p));   // optimiste
    setAction(true);
    try {
      await axios.put(`${DOMAIN}/api/photos-partage/${modele.id_produit}/ordre`, { ordre: cur.map((x) => x.id) });
    } catch (e) {
      // Retour à l'ordre PRÉCÉDENT depuis l'instantané — pas de re-fetch, qui
      // pourrait vider la liste si le réseau est aussi coupé pour le GET.
      toast.error(e.response?.data?.error || 'Réordonnancement impossible.');
      setPhotos((p) => (p ? { ...p, list: avant } : p));
    } finally { setAction(false); }
  };

  // --- Glisser-déposer : réordonnancement des slots (drag INTERNE) -----------
  const onSlotDragStart = (e, idx) => {
    if (action) { e.preventDefault(); return; }
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) { /* vieux navigateurs */ }
  };
  const onSlotDragOver = (e) => {
    if (dragIndex === null) return;      // laisse passer les dépôts de fichiers
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onSlotDrop = (e, idx) => {
    if (dragIndex === null) return;
    e.preventDefault();
    e.stopPropagation();
    const from = dragIndex;
    setDragIndex(null);
    reordonner(from, idx);
  };
  const onSlotDragEnd = () => setDragIndex(null);

  // --- Glisser-déposer : dépôt d'images pour AJOUTER (drag EXTERNE) ----------
  const contientFichiers = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onZoneDragOver = (e) => {
    if (dragIndex !== null || !contientFichiers(e)) return;  // réordonnancement interne : ignorer
    e.preventDefault();                                       // toujours : sinon le navigateur ouvre le fichier
    // Au maximum de photos : on refuse le dépôt (curseur « interdit ») et on
    // n'affiche PAS l'invite « déposez ici » (elle serait trompeuse).
    const plein = (photos?.list?.length || 0) >= (photos?.max || 5);
    e.dataTransfer.dropEffect = plein ? 'none' : 'copy';
    if (!plein && !fileDrop) setFileDrop(true);
  };
  const onZoneDragLeave = (e) => {
    if (!fileDrop) return;
    if (e.currentTarget.contains(e.relatedTarget)) return;   // entrée dans un enfant : rester actif
    setFileDrop(false);
  };
  const onZoneDrop = (e) => {
    if (dragIndex !== null || !contientFichiers(e)) return;
    e.preventDefault();
    setFileDrop(false);
    const tous = Array.from(e.dataTransfer.files || []);
    const images = tous.filter((f) => /^image\//.test(f.type || ''));
    if (images.length) televerserPlusieurs(images);
    else if (tous.length) toast.info('Seules les images sont acceptées.');
  };

  if (ready && !autorise) {
    return (
      <NdoePage className="pp-page">
        <NdoePageHeader eyebrow="Catalogue" title="Photos à partager" />
        <NdoeEmpty icon="🔒" title="Accès non accordé"
          text="Demandez à l'administrateur d'activer « Gérer les photos à partager » pour votre compte (matrice des permissions)." />
      </NdoePage>
    );
  }

  const liste = photos?.list || [];
  const aUnDefaut = liste.some((p) => Number(p.est_defaut) === 1);
  const max = photos?.max || 5;

  return (
    <NdoePage className="pp-page">
      <NdoePageHeader
        eyebrow="Catalogue"
        title="Photos à partager"
        subtitle="Jusqu'à 5 photos « mannequin » par modèle — affichées en priorité dans le Catalogue de partage. Glissez pour réordonner, déposez des images pour ajouter."
      />

      <div className="pp-filtres">
        <Input allowClear prefix={<SearchOutlined />} placeholder="Nom ou référence du modèle…"
          value={q} onChange={(e) => setQ(e.target.value)} className="pp-f pp-f-q" />
        <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="Catégorie" options={refs.categories} value={filters.id_categorie} onChange={setF('id_categorie')} showSearch optionFilterProp="label" className="pp-f" />
        <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="Marque" options={refs.marques} value={filters.id_marque} onChange={setF('id_marque')} showSearch optionFilterProp="label" className="pp-f" />
        <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="Couleur" options={refs.couleurs} value={filters.id_couleur} onChange={setF('id_couleur')} showSearch optionFilterProp="label" className="pp-f" />
        <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="Pointure" options={POINTURES.map((pt) => ({ value: pt, label: `P${pt}` }))} value={filters.taille} onChange={setF('taille')} className="pp-f pp-f-pt" />
        <Select value={tri} onChange={setTri} className="pp-f pp-f-tri" title="Trier les modèles"
          options={[
            { value: '', label: 'Tri : A→Z' },
            { value: 'sales_desc', label: 'Meilleures ventes (90 j)' },
            { value: 'dormant_stock', label: 'Dort en stock (0 vente 90 j)' },
            { value: 'never_sold', label: 'Jamais vendu' },
            { value: 'restock', label: 'À réassortir (se vend, stock bas)' },
            { value: 'last_sale', label: 'Dernières ventes' },
            { value: 'last_receipt', label: 'Derniers arrivages' },
          ]} />
      </div>

      {loading ? <NdoeLoading rows={4} />
        : erreur ? <NdoeError text={erreur} onRetry={() => setQ((s) => s)} />
        : !modeles.length ? <NdoeEmpty icon="👟" title="Aucun modèle pour cette recherche" />
        : (
          <div className="pp-grille">
            {modeles.map((m) => (
              <button type="button" key={m.id_produit} className="pp-carte" onClick={() => chargerPhotos(m)}>
                <div className="pp-thumb"><NdoeImage src={m.img_partage || m.img} alt={m.nom_produit || 'Modèle'} size="lg" preview={false} /></div>
                {m.img_partage && <Tag className="pp-badge" icon={<PictureOutlined />} color="gold">mannequin</Tag>}
                <span className="pp-ref num">{m.code_produit || '—'}</span>
                <span className="pp-nom">{m.nom_produit}</span>
                <span className="pp-sub">{m.marque || ''}</span>
              </button>
            ))}
          </div>
        )}

      <Modal
        open={!!modele}
        onCancel={() => { setModele(null); setPhotos(null); setDragIndex(null); setFileDrop(false); setAction(false); }}
        footer={null}
        width={720}
        title={modele ? `Photos à partager — ${modele.nom_produit}` : ''}
        destroyOnClose
      >
        {photos?.loading ? <div className="pp-chargement"><Spin /></div> : (
          <>
            <div className="pp-editeur-head">
              <div className="pp-catalogue-ref">
                <NdoeImage src={modele?.img} alt="Photo catalogue" size="md" preview={false} />
                <div>
                  <b>Photo catalogue</b>
                  <small>{aUnDefaut ? 'Remplacée au partage par la photo par défaut ★' : 'Utilisée par défaut au partage'}</small>
                  {aUnDefaut && (
                    <button type="button" className="pp-lien" disabled={action} onClick={() => definirDefaut(null)}>
                      Préférer la photo catalogue
                    </button>
                  )}
                </div>
              </div>
              <span className="pp-compteur num">{liste.length}/{max} photo(s)</span>
            </div>

            {liste.length > 1 && (
              <p className="pp-aide"><HolderOutlined /> Glissez une photo pour changer l'ordre (ou les flèches). L'étoile ★ reste la photo par défaut.</p>
            )}

            <div
              className={`pp-slots ${fileDrop ? 'is-file-drop' : ''}`}
              onDragOver={onZoneDragOver}
              onDragLeave={onZoneDragLeave}
              onDrop={onZoneDrop}
            >
              {fileDrop && <div className="pp-drop-hint"><UploadOutlined /> Déposez les images ici</div>}
              {liste.map((p, idx) => (
                <div
                  key={p.id}
                  className={`pp-slot ${Number(p.est_defaut) === 1 ? 'is-defaut' : ''} ${dragIndex === idx ? 'is-dragging' : ''}`}
                  draggable={!action}
                  onDragStart={(e) => onSlotDragStart(e, idx)}
                  onDragOver={onSlotDragOver}
                  onDrop={(e) => onSlotDrop(e, idx)}
                  onDragEnd={onSlotDragEnd}
                  title="Glissez pour réordonner"
                >
                  <span className="pp-poignee" aria-hidden="true"><HolderOutlined /></span>
                  <NdoeImage src={p.chemin} alt="Photo à partager" size="lg" preview />
                  <div className="pp-slot-actions">
                    <Tooltip title="Reculer">
                      <button type="button" className="pp-btn pp-btn-move" disabled={action || idx === 0}
                        onClick={() => reordonner(idx, idx - 1)} aria-label="Reculer"><LeftOutlined /></button>
                    </Tooltip>
                    <Tooltip title={Number(p.est_defaut) === 1 ? 'Photo par défaut au partage' : 'Définir comme photo par défaut'}>
                      <button type="button" className="pp-btn" disabled={action || Number(p.est_defaut) === 1}
                        onClick={() => definirDefaut(p.id)}>
                        {Number(p.est_defaut) === 1 ? <StarFilled className="pp-star" /> : <StarOutlined />}
                      </button>
                    </Tooltip>
                    <Tooltip title="Supprimer cette photo">
                      <button type="button" className="pp-btn pp-btn-danger" disabled={action}
                        onClick={() => Modal.confirm({
                          title: 'Supprimer cette photo ?',
                          content: Number(p.est_defaut) === 1 ? 'La photo suivante deviendra la photo par défaut.' : undefined,
                          okText: 'Supprimer', cancelText: 'Annuler', okButtonProps: { danger: true },
                          onOk: () => supprimer(p.id),
                        })}>
                        <DeleteOutlined />
                      </button>
                    </Tooltip>
                    <Tooltip title="Avancer">
                      <button type="button" className="pp-btn pp-btn-move" disabled={action || idx === liste.length - 1}
                        onClick={() => reordonner(idx, idx + 1)} aria-label="Avancer"><RightOutlined /></button>
                    </Tooltip>
                  </div>
                </div>
              ))}
              {liste.length < max && (
                <label className={`pp-slot pp-slot-ajout ${action ? 'is-off' : ''}`}>
                  <input type="file" accept="image/*" hidden multiple disabled={action}
                    onChange={(e) => { const fs = Array.from(e.target.files || []); if (fs.length) televerserPlusieurs(fs); e.target.value = ''; }} />
                  <UploadOutlined />
                  <span>{action ? 'Envoi…' : 'Ajouter des photos'}</span>
                  <small>{liste.length === 0 ? 'La première devient la photo par défaut' : 'Cliquez ou déposez des images'}</small>
                </label>
              )}
            </div>
          </>
        )}
      </Modal>
    </NdoePage>
  );
};

export default PhotosPartage;
